import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchWhatsAppMessages,
  fetchWhatsAppClicks,
  fetchWhatsAppBlocked,
  unblockWhatsAppNumber,
  fetchWhatsAppStats,
} from '../WhatsApp-Marketing/shared/whatsappApi.js';

// Server state for the WhatsApp Records page (four independent tabs) and the
// Dashboard's stats tile.

const asMessage = (error, fallback) => error?.message || fallback;

// The Delivered/Read and Replies tabs both read the messages endpoint with
// different filters, so they get separate slots rather than sharing one list.
export const loadDeliveredRead = createAsyncThunk(
  'whatsappRecords/loadDeliveredRead',
  async (_, { rejectWithValue }) => {
    try {
      const { messages } = await fetchWhatsAppMessages({
        direction: 'outbound',
        status: 'delivered,read',
        limit: 100,
      });
      return messages;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load messages'));
    }
  }
);

export const loadReplies = createAsyncThunk(
  'whatsappRecords/loadReplies',
  async (_, { rejectWithValue }) => {
    try {
      const { messages } = await fetchWhatsAppMessages({ direction: 'inbound', limit: 100 });
      return messages;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load replies'));
    }
  }
);

export const loadBounced = createAsyncThunk(
  'whatsappRecords/loadBounced',
  async (_, { rejectWithValue }) => {
    try {
      const { messages } = await fetchWhatsAppMessages({
        direction: 'outbound',
        status: 'failed,undelivered',
        limit: 100,
      });
      return messages;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load bounced messages'));
    }
  }
);

export const loadClicks = createAsyncThunk(
  'whatsappRecords/loadClicks',
  async (limit = 100, { rejectWithValue }) => {
    try {
      const { clicks } = await fetchWhatsAppClicks(limit);
      return clicks;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load clicks'));
    }
  }
);

export const loadBlocked = createAsyncThunk(
  'whatsappRecords/loadBlocked',
  async (limit = 100, { rejectWithValue }) => {
    try {
      const { blocked } = await fetchWhatsAppBlocked(limit);
      return blocked;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load blocked numbers'));
    }
  }
);

export const unblockNumber = createAsyncThunk(
  'whatsappRecords/unblockNumber',
  async (phone, { dispatch, rejectWithValue }) => {
    try {
      await unblockWhatsAppNumber(phone);
      dispatch(loadBlocked());
      return phone;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to unblock number'));
    }
  }
);

export const loadStats = createAsyncThunk(
  'whatsappRecords/loadStats',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchWhatsAppStats();
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load stats'));
    }
  }
);

const initialState = {
  deliveredRead: [],
  deliveredReadLoading: true,
  replies: [],
  repliesLoading: true,
  bounced: [],
  bouncedLoading: true,
  clicks: [],
  clicksLoading: true,
  blocked: [],
  blockedLoading: true,
  stats: null,
  statsLoading: true,
  error: null,
};

// Each tab tracks its own loading flag so one refresh does not blank the others.
const pair = (builder, thunk, key, loadingKey) =>
  builder
    .addCase(thunk.pending, (state) => {
      state[loadingKey] = true;
      state.error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[loadingKey] = false;
      state[key] = action.payload;
    })
    .addCase(thunk.rejected, (state, action) => {
      state[loadingKey] = false;
      state.error = action.payload;
    });

const whatsappRecordsSlice = createSlice({
  name: 'whatsappRecords',
  initialState,
  reducers: {
    clearRecordsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    pair(builder, loadDeliveredRead, 'deliveredRead', 'deliveredReadLoading');
    pair(builder, loadReplies, 'replies', 'repliesLoading');
    pair(builder, loadBounced, 'bounced', 'bouncedLoading');
    pair(builder, loadClicks, 'clicks', 'clicksLoading');
    pair(builder, loadBlocked, 'blocked', 'blockedLoading');
    pair(builder, loadStats, 'stats', 'statsLoading');
  },
});

export const { clearRecordsError } = whatsappRecordsSlice.actions;

export default whatsappRecordsSlice.reducer;
